import { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertTriangle, Loader2, ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const OpeningBalanceAuditModal = ({ open, onOpenChange, onSelectCustomer }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const runAudit = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/customers/audit/opening-balance-corruption`);
      setData(res.data);
      if (res.data.suspect_count === 0) {
        toast.success("All customer opening balances look clean — no corruption detected");
      } else {
        toast.warning(`Found ${res.data.suspect_count} customer(s) with suspect opening balances`);
      }
    } catch (e) {
      toast.error("Audit failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !data) {
      runAudit();
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="opening-balance-audit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-orange-600" />
            Opening Balance Corruption Audit
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900">
            <div className="flex gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                Flags customers whose stored opening balance has more than 2 decimal places
                (a signature of the historical <code className="bg-amber-100 px-1 rounded">$inc account_balance</code> bug
                in sales conversion / bulk-assign / quotation conversion endpoints, now fixed).
                Review each suspect and adjust the opening balance via the customer&apos;s edit dialog if needed.
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {data ? (
                <>Scanned <b>{data.total_customers_scanned}</b> customers · <b className="text-orange-600">{data.suspect_count}</b> flagged</>
              ) : (
                <>Click rescan to refresh</>
              )}
            </div>
            <Button onClick={runAudit} disabled={loading} variant="outline" data-testid="audit-rescan-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Rescan
            </Button>
          </div>

          {loading ? (
            <div className="py-12 text-center text-gray-500">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
              Running audit...
            </div>
          ) : data && data.suspects.length === 0 ? (
            <div className="py-12 text-center text-green-700">
              <ShieldCheck className="h-10 w-10 mx-auto mb-2" />
              <div className="font-medium">No suspect balances found</div>
              <div className="text-sm text-gray-500">All customer opening balances look clean.</div>
            </div>
          ) : data ? (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Account #</TableHead>
                    <TableHead className="text-right">Stored Opening</TableHead>
                    <TableHead className="text-right">Current Balance</TableHead>
                    <TableHead className="text-right">If Opening Was R 0</TableHead>
                    <TableHead className="text-right">Opening for R 0 Current</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.suspects.map((s) => (
                    <TableRow key={s.customer_id} data-testid={`audit-row-${s.customer_id}`}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="text-gray-600">{s.account_number || "—"}</TableCell>
                      <TableCell className="text-right font-mono text-orange-700">
                        R {s.stored_opening_balance.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        R {(s.current_calculated_balance ?? 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-gray-600">
                        R {(s.balance_with_zero_opening ?? 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-blue-700">
                        R {(s.suggested_opening_for_zero_current ?? 0).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {onSelectCustomer && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onSelectCustomer(s)}
                            data-testid={`audit-edit-btn-${s.customer_id}`}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}

          {data && (
            <div className="text-xs text-gray-500 italic">
              <b>Opening for R 0 Current</b> = the opening value that would make the customer&apos;s
              current calculated balance exactly R 0. Compare to the true expected balance to choose
              the correct opening to enter.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OpeningBalanceAuditModal;
